import React, { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './settings-dialog.css';

/** Tab, Shift+Tab and Escape have to keep working inside a modal. */
const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * The product's modal shell.
 *
 * Portalled to <body> on purpose. `.pnid-sidebar` is `position: sticky`, which
 * always forms a stacking context, so a dialog rendered in place inside the
 * rail is trapped there and the page paints over it at ANY z-index. Portal
 * first, then z-index.
 */
export default function PnidModal({
  open,
  title,
  onClose,
  children,
  footer,
  width,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Overrides the default 420px surface for content that needs more room. */
  width?: number;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  /* Whatever opened the dialog gets focus back when it closes, so a keyboard
     user is not dumped at the top of the document. */
  const openerRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement as HTMLElement | null;
    const first = surfaceRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    return () => {
      const opener = openerRef.current;
      /* Only worth restoring to a real, still-mounted control. Menus in this
         app leave focus on <body> when they close, and focusing <body> back is
         the same as doing nothing — the caller restores in that case. */
      if (opener && opener.isConnected && opener !== document.body) opener.focus();
    };
  }, [open]);

  /* The page behind a modal must not scroll away under it. */
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = Array.from(
      surfaceRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [],
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="pnid-modal-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        className="pnid-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={surfaceRef}
        style={width ? { maxWidth: width } : undefined}
      >
        <div className="pnid-modal__head">
          <h2 className="pnid-modal__title" id={titleId}>{title}</h2>
          <button
            type="button"
            className="pnid-modal__close"
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()}`}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden focusable="false">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="pnid-modal__body">{children}</div>
        {footer && <div className="pnid-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
